/**
 * 💎 صفحة إدارة اشتراك جمعية اتحاد ملاك (Mullak SaaS)
 *
 * يصلها مدير الجمعية من /a/{slug}/subscription
 * تعرض: الباقة الحالية، أيام التجربة المتبقية، عدد الوحدات،
 *       خيار الترقية/التخفيض، سجل المعاملات.
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Button, Badge } from '@img/ui';
import { api, type ApiSuccess } from '../lib/api';

interface SubscriptionData {
  id: string;
  name: string;
  slug: string;
  totalUnits: number;
  subscriptionTier: 'FREE' | 'BASIC' | 'PRO' | 'ENTERPRISE';
  subscriptionStatus: 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED' | 'EXPIRED';
  trialEndsAt: string | null;
  subscriptionStartedAt: string | null;
  subscriptionEndsAt: string | null;
  monthlyFee: number | string | null;
  customBranding: boolean;
  prioritySupport: boolean;
  trialDaysLeft: number | null;
  isOverUnitLimit: boolean;
  plan: {
    code: string;
    nameAr: string;
    monthlyPrice: number | null;
    yearlyPrice: number | null;
    maxUnits: number | null;
    features: string[];
  };
}

interface PaymentRow {
  id: string;
  tier: string;
  period: string;
  amount: number | string;
  status: string;
  periodStartAt: string;
  periodEndAt: string;
  paidAt: string | null;
  invoiceNumber: string | null;
  createdAt: string;
}

export function AssociationSubscriptionPage() {
  const { associationId } = useParams<{ associationId: string }>();
  const qc = useQueryClient();
  const [period, setPeriod] = useState<'monthly' | 'yearly'>('monthly');

  const { data, isLoading } = useQuery({
    queryKey: ['assoc-subscription', associationId],
    queryFn: () => api.get<ApiSuccess<SubscriptionData>>(`/api/associations/${associationId}/subscription`),
    enabled: !!associationId,
  });

  const { data: paymentsRes } = useQuery({
    queryKey: ['assoc-payments', associationId],
    queryFn: () => api.get<ApiSuccess<{ payments: PaymentRow[] }>>(`/api/associations/${associationId}/subscription/payments`),
    enabled: !!associationId,
  });

  const changePlan = useMutation({
    mutationFn: (tier: 'FREE' | 'BASIC' | 'PRO' | 'ENTERPRISE') =>
      api.post(`/api/associations/${associationId}/subscription/change-plan`, { tier, period }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assoc-subscription', associationId] });
      qc.invalidateQueries({ queryKey: ['assoc-payments', associationId] });
    },
  });

  if (isLoading || !data?.data) {
    return <div className="text-center py-16 text-gray-500">⟳ جاري التحميل…</div>;
  }
  const sub = data.data;
  const payments = paymentsRes?.data?.payments ?? [];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-6">
        <Link to={`/a/${sub.slug}`} className="text-sm text-navy-dark hover:underline">
          ← العودة لـ {sub.name}
        </Link>
        <h1 className="text-3xl font-black text-navy-dark mt-2">💎 اشتراك المنصة</h1>
        <p className="text-gray-600">إدارة باقة جمعية اتحاد الملاك على منصة شركة صورة العقارية</p>
      </div>

      {/* 📊 الحالة الحالية */}
      <Card className="mb-6">
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatusBox label="الباقة الحالية" value={sub.plan.nameAr} color="navy" />
            <StatusBox
              label="الحالة"
              value={
                sub.subscriptionStatus === 'TRIAL' ? `تجريبية (${sub.trialDaysLeft} يوم)` :
                sub.subscriptionStatus === 'ACTIVE' ? 'فعّالة ✓' :
                sub.subscriptionStatus === 'PAST_DUE' ? 'متأخرة' :
                sub.subscriptionStatus === 'CANCELLED' ? 'ملغاة' : 'منتهية'
              }
              color={sub.subscriptionStatus === 'ACTIVE' ? 'green' : sub.subscriptionStatus === 'TRIAL' ? 'amber' : 'red'}
            />
            <StatusBox
              label="عدد الوحدات"
              value={`${sub.totalUnits}${sub.plan.maxUnits !== null ? ` / ${sub.plan.maxUnits}` : ''}`}
              color={sub.isOverUnitLimit ? 'red' : 'navy'}
            />
            <StatusBox
              label="الرسوم الشهرية"
              value={sub.monthlyFee && Number(sub.monthlyFee) > 0 ? `${Number(sub.monthlyFee).toLocaleString('en-US')} ر.س` : 'مجاناً'}
              color="gold"
            />
          </div>

          {sub.isOverUnitLimit && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800">
              ⚠️ <b>تجاوزت حد الباقة الحالية.</b> رقّي لباقة أعلى لتجنّب تقييد الميزات.
            </div>
          )}
          {sub.subscriptionStatus === 'TRIAL' && sub.trialDaysLeft !== null && sub.trialDaysLeft <= 3 && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
              ⏰ <b>تنتهي فترتك التجريبية خلال {sub.trialDaysLeft} يوم.</b> اشترك الآن لتجنب فقدان الميزات.
            </div>
          )}
        </CardBody>
      </Card>

      {/* 💎 الباقات */}
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="text-xl font-black text-navy-dark">اختر باقتك</h2>
          <div className="bg-gray-100 rounded-xl p-1 inline-flex">
            <button
              onClick={() => setPeriod('monthly')}
              className={`px-4 py-1.5 rounded-lg text-sm font-bold transition ${period === 'monthly' ? 'bg-white shadow text-navy-dark' : 'text-gray-500'}`}
            >
              شهري
            </button>
            <button
              onClick={() => setPeriod('yearly')}
              className={`px-4 py-1.5 rounded-lg text-sm font-bold transition ${period === 'yearly' ? 'bg-white shadow text-navy-dark' : 'text-gray-500'}`}
            >
              سنوي
              <span className="ms-1 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">شهرين مجاناً</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {(['FREE', 'BASIC', 'PRO', 'ENTERPRISE'] as const).map((tier) => (
            <TierCard
              key={tier}
              tier={tier}
              isCurrent={sub.subscriptionTier === tier}
              period={period}
              loading={changePlan.isPending}
              onSelect={() => {
                if (tier === 'ENTERPRISE') {
                  window.location.href = '/contact?topic=enterprise';
                  return;
                }
                if (confirm(`تأكيد التحويل لباقة ${TIER_INFO[tier].nameAr}؟`)) {
                  changePlan.mutate(tier);
                }
              }}
            />
          ))}
        </div>
      </div>

      {/* 📋 سجل المعاملات */}
      <Card>
        <CardBody>
          <h2 className="text-xl font-black text-navy-dark mb-3">📋 سجل المعاملات</h2>
          {payments.length === 0 ? (
            <p className="text-gray-500 text-sm">لا توجد معاملات بعد.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-right">
                  <tr>
                    <th className="px-3 py-2 font-bold">رقم الفاتورة</th>
                    <th className="px-3 py-2 font-bold">الباقة</th>
                    <th className="px-3 py-2 font-bold">المبلغ</th>
                    <th className="px-3 py-2 font-bold">الفترة</th>
                    <th className="px-3 py-2 font-bold">الحالة</th>
                    <th className="px-3 py-2 font-bold">التاريخ</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.id} className="border-t border-gray-100">
                      <td className="px-3 py-2 font-mono text-xs">{p.invoiceNumber ?? '—'}</td>
                      <td className="px-3 py-2">{TIER_INFO[p.tier as keyof typeof TIER_INFO]?.nameAr ?? p.tier}</td>
                      <td className="px-3 py-2 font-bold">{Number(p.amount).toLocaleString('en-US')} ر.س</td>
                      <td className="px-3 py-2 text-xs">{p.period === 'monthly' ? 'شهري' : 'سنوي'}</td>
                      <td className="px-3 py-2">
                        <Badge color={p.status === 'paid' ? 'green' : p.status === 'pending' ? 'amber' : 'red'}>
                          {p.status === 'paid' ? 'مدفوعة' : p.status === 'pending' ? 'بانتظار الدفع' : 'مرفوضة'}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {new Date(p.createdAt).toLocaleDateString('ar-SA')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

const TIER_INFO = {
  FREE:       { nameAr: 'مجاني',     monthly: 0,    yearly: 0,     maxUnits: '10',   color: 'gray' },
  BASIC:      { nameAr: 'أساسية',     monthly: 299,  yearly: 2990,  maxUnits: '50',   color: 'navy' },
  PRO:        { nameAr: 'احترافية',   monthly: 999,  yearly: 9990,  maxUnits: '200',  color: 'gold' },
  ENTERPRISE: { nameAr: 'مؤسسات',    monthly: null, yearly: null,  maxUnits: '∞',    color: 'emerald' },
} as const;

function TierCard({
  tier, isCurrent, period, loading, onSelect,
}: {
  tier: keyof typeof TIER_INFO;
  isCurrent: boolean;
  period: 'monthly' | 'yearly';
  loading: boolean;
  onSelect: () => void;
}) {
  const info = TIER_INFO[tier];
  const price = period === 'monthly' ? info.monthly : info.yearly;
  return (
    <div className={`relative bg-white border-2 ${isCurrent ? 'border-emerald-500 shadow-lg' : tier === 'PRO' ? 'border-gold' : 'border-gray-200'} rounded-2xl p-5 flex flex-col`}>
      {isCurrent && (
        <span className="absolute -top-3 right-4 bg-emerald-500 text-white text-[10px] font-black px-3 py-1 rounded-full">
          ✓ الباقة الحالية
        </span>
      )}
      <div className="text-center mb-4">
        <h3 className="text-lg font-black text-navy-darker mb-1">{info.nameAr}</h3>
        <div className="flex items-baseline justify-center gap-1 mt-3">
          {price !== null ? (
            <>
              <span className="text-3xl font-black text-navy-dark tabular-nums">{price.toLocaleString('en-US')}</span>
              <span className="text-xs text-gray-500">ر.س / {period === 'monthly' ? 'شهر' : 'سنة'}</span>
            </>
          ) : (
            <span className="text-2xl font-black text-navy-dark">مخصّص</span>
          )}
        </div>
        <div className="text-[10px] text-gray-500 mt-1">حتى {info.maxUnits} وحدة</div>
      </div>
      <Button
        variant={isCurrent ? 'ghost' : tier === 'PRO' ? 'gold' : 'primary'}
        size="sm"
        className="w-full mt-auto"
        onClick={onSelect}
        disabled={isCurrent || loading}
        loading={loading}
      >
        {isCurrent ? 'الباقة الحالية' : tier === 'ENTERPRISE' ? 'تواصل معنا' : 'اختر هذه الباقة'}
      </Button>
    </div>
  );
}

function StatusBox({ label, value, color }: { label: string; value: string; color: 'navy' | 'gold' | 'green' | 'red' | 'amber' }) {
  const colorClasses = {
    navy: 'bg-navy-soft/30 text-navy-dark',
    gold: 'bg-gold-soft text-gold-dark',
    green: 'bg-emerald-50 text-emerald-700',
    red: 'bg-red-50 text-red-700',
    amber: 'bg-amber-50 text-amber-800',
  };
  return (
    <div className={`rounded-xl p-4 ${colorClasses[color]}`}>
      <div className="text-xs font-bold opacity-70 mb-1">{label}</div>
      <div className="text-lg font-black">{value}</div>
    </div>
  );
}
